// Office invoice detail page (/office/invoices/:id). Opens one cached invoice with
// its real Xero number, customer/site, dates, status and amount, plus a best-effort
// live Xero block (line items + amount paid/outstanding) and the reverse back-reference
// to the job or call-out it covers. Read-only; admins authorise a draft from the list.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import type { InvoiceDetail } from "../../lib/api";
import { RecordHeader } from "../../components/RecordHeader";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { SendPaymentLinkModal, DraftChaserModal, CreditNoteModal, GenerateStatementModal } from "./InvoiceActionModals";

function gbp(n: number | null | undefined): string {
  if (n == null) return "-";
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// "Call-out" / "Repair" / "Service" etc. for the reverse-link label.
function jobTypeLabel(type: string): string {
  // Session ANG: the fallback is the raw enum key with its first letter capitalised, so an unnamed
  // type prints "Tech_visit" -- a machine key, underscore and all, on a money screen. The two ALW
  // types are named here for the same reason, being the same inherited miss.
  const map: Record<string, string> = {
    callout: "Call-out",
    repair: "Repair",
    service: "Service visit",
    loler: "LOLER visit",
    condition_report: "Condition report",
    site_inspection: "Site inspection",
    tech_visit: "Tech visit",
    insurance_check: "Insurance check",
    safed_test: "SAFed test",
  };
  return map[type] ?? type.charAt(0).toUpperCase() + type.slice(1);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">{label}</p>
      <p className="mt-1 text-sm text-ele-strong">{children}</p>
    </div>
  );
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error" | "notfound">("loading");
  // Phase 8 (Session ATJ): which action modal, if any, is open, and a one-line confirmation once
  // an action has actually done something (never a silent success).
  const [openModal, setOpenModal] = useState<"payment-link" | "chaser" | "credit-note" | "statement" | null>(null);
  const [doneMessage, setDoneMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let live = true;
    setState("loading");
    api.invoices
      .get(id)
      .then((inv) => {
        if (live) {
          setInvoice(inv);
          setState("ready");
        }
      })
      .catch((err) => {
        if (live) setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
      });
    return () => {
      live = false;
    };
  }, [id]);

  function actionDone(message: string) {
    setOpenModal(null);
    setDoneMessage(message);
    // Re-fetch so the status/link fields on the page reflect what just happened.
    if (id) api.invoices.get(id).then(setInvoice).catch(() => undefined);
  }

  const payable = invoice?.status === "authorised" || invoice?.status === "overdue";

  return (
    <div>
      {/* The back link is the record header's own once the invoice is loaded; before that (loading,
          not found, failed) the same link stands alone so the office is never stranded. */}
      {state !== "ready" && (
        <Link to="/office/invoices" className="inline-flex items-center gap-1 text-xs text-ele-body opacity-70 transition hover:text-ele-purple hover:opacity-100">
          <span aria-hidden>&larr;</span> Invoices
        </Link>
      )}

      {state === "loading" ? (
        <Loader shape="panel" className="mt-4" />
      ) : state === "notfound" ? (
        <p role="alert" className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          That invoice could not be found.
        </p>
      ) : state === "error" ? (
        <p role="alert" className="mt-4 rounded-ele border border-ele-danger bg-ele-surface p-6 text-center text-ele-strong">
          Could not load this invoice. Please try again in a moment.
        </p>
      ) : invoice ? (
        <div className="mt-3">
          <RecordHeader
            className="mb-4"
            backTo="/office/invoices"
            backLabel="Invoices"
            title={invoice.invoiceNumber ? `Invoice ${invoice.invoiceNumber}` : "Invoice"}
            status={<StatusPill domain="invoice" status={invoice.status} />}
            actions={
              <a
                href={`https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${encodeURIComponent(invoice.xeroInvoiceId)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="max-sm:min-h-11 flex items-center shrink-0 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-blue hover:text-ele-blue"
              >
                View in Xero
              </a>
            }
          />

          {/* RCP1: did the customer's payment receipt go out. One quiet line, worked out live on the server (sent and to whom, waiting, failed,
              or not applicable); only a failed one is drawn in the danger colour, and the words always say which it is. */}
          {invoice.receipt && (
            <p data-testid="payment-receipt-line" data-receipt-state={invoice.receipt.state} className={`mb-4 text-sm ${invoice.receipt.state === "failed" ? "text-ele-danger" : "text-ele-body"}`}>
              {invoice.receipt.line}
            </p>
          )}

          {/* Phase 8 (Session ATJ): money beyond Xero drafts. Every action previews first. */}
          <div className="mb-4 flex flex-wrap gap-2">
            {payable && (
              <button
                type="button"
                onClick={() => setOpenModal("payment-link")}
                className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
              >
                Send payment link
              </button>
            )}
            {payable && (
              <button
                type="button"
                onClick={() => setOpenModal("chaser")}
                className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
              >
                Draft chaser
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpenModal("credit-note")}
              className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
            >
              Raise credit note
            </button>
            {invoice.customerId && (
              <button
                type="button"
                onClick={() => setOpenModal("statement")}
                className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
              >
                Generate statement
              </button>
            )}
          </div>
          {doneMessage && (
            <p role="status" className="mb-4 rounded-ele border border-ele-green bg-ele-surface p-3 text-sm text-ele-strong">
              {doneMessage}
            </p>
          )}

          <div className="grid grid-cols-1 gap-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Customer">
              {invoice.customerId ? (
                <Link to={`/office/customers/${encodeURIComponent(invoice.customerId)}`} className="text-ele-green underline-offset-2 hover:underline">
                  {invoice.customerName}
                </Link>
              ) : (
                invoice.customerName
              )}
            </Field>
            <Field label="Site">
              {invoice.siteId ? (
                <Link to={`/office/sites/${encodeURIComponent(invoice.siteId)}`} className="text-ele-green underline-offset-2 hover:underline">
                  {invoice.siteName}
                </Link>
              ) : (
                invoice.siteName ?? "-"
              )}
            </Field>
            <Field label="Invoice number">{invoice.invoiceNumber ?? "Not yet numbered"}</Field>
            <Field label="Issue date">{formatDate(invoice.xeroDetail?.issueDate ?? null)}</Field>
            <Field label="Due date">{formatDate(invoice.dueDate)}</Field>
            <Field label="Total">{gbp(invoice.xeroDetail?.total ?? invoice.amount)}</Field>
            <Field label="Paid">{gbp(invoice.xeroDetail?.amountPaid)}</Field>
            <Field label="Outstanding">{gbp(invoice.xeroDetail?.amountDue)}</Field>
          </div>

          {/* Reverse back-reference: the job or call-out this invoice covers. */}
          {(invoice.linkedJob || invoice.linkedCallout) && (
            <div className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
              <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">Linked to</p>
              {invoice.linkedJob && (
                <p className="mt-1 text-sm text-ele-strong">
                  <Link to={`/office/jobsheets?job=${encodeURIComponent(invoice.linkedJob.jobId)}`} className="text-ele-green underline-offset-2 hover:underline">
                    {jobTypeLabel(invoice.linkedJob.type)}
                    {invoice.linkedJob.siteName ? ` at ${invoice.linkedJob.siteName}` : ""}
                    {invoice.linkedJob.eleNumber ? ` (${invoice.linkedJob.eleNumber})` : ""}
                    {invoice.linkedJob.completedAt ? `, ${formatDate(invoice.linkedJob.completedAt)}` : ""}
                  </Link>
                </p>
              )}
              {invoice.linkedCallout && (
                <p className="mt-1 text-sm text-ele-strong">
                  <Link
                    to={
                      invoice.linkedCallout.jobSheetJobId
                        ? `/office/callouts?job=${encodeURIComponent(invoice.linkedCallout.jobSheetJobId)}`
                        : "/office/callouts"
                    }
                    className="text-ele-green underline-offset-2 hover:underline"
                  >
                    Call-out{invoice.linkedCallout.jobRef ? ` ${invoice.linkedCallout.jobRef}` : ""}
                    {invoice.linkedCallout.siteName ? ` at ${invoice.linkedCallout.siteName}` : ""}
                    {invoice.linkedCallout.reportedAt ? `, ${formatDate(invoice.linkedCallout.reportedAt)}` : ""}
                  </Link>
                </p>
              )}
            </div>
          )}

          {/* Line items: only when a live Xero read succeeded (the cache stores no lines). */}
          {invoice.xeroDetail ? (
            invoice.xeroDetail.lineItems.length > 0 ? (
              <div className="mt-4 overflow-hidden rounded-ele border border-ele-purple-accent">
                <table className="ele-list-table w-full text-left text-sm">
                  <colgroup>
                    <col style={{ width: "58%" }} />
                    <col style={{ width: "12%" }} />
                    <col className="max-md:hidden" style={{ width: "15%" }} />
                    <col style={{ width: "15%" }} />
                  </colgroup>
                  <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                    <tr>
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="max-md:hidden px-3 py-2 text-right">Unit (ex VAT)</th>
                      <th className="px-3 py-2 text-right">Line (ex VAT)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.xeroDetail.lineItems.map((l, i) => (
                      <tr key={i} className="border-t border-ele-purple-accent">
                        <td className="cell-wrap px-3 py-2 align-top text-ele-strong">{l.description}</td>
                        <td className="px-3 py-2 text-right align-top">{l.quantity}</td>
                        <td className="max-md:hidden px-3 py-2 text-right align-top">{gbp(l.unitAmount)}</td>
                        <td className="px-3 py-2 text-right align-top text-ele-strong">{gbp(l.lineAmount ?? l.unitAmount * l.quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-4 text-sm text-ele-body">This invoice has no line items in Xero.</p>
            )
          ) : (
            <p className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-sm text-ele-body">
              Line items and the amounts paid and outstanding are read live from Xero and are not available right now. Open the invoice in Xero to see the full breakdown.
            </p>
          )}
        </div>
      ) : null}

      {invoice && openModal === "payment-link" && (
        <SendPaymentLinkModal
          invoiceId={invoice.id}
          onClose={() => setOpenModal(null)}
          onDone={() => actionDone("Payment link raised and the email is waiting in Gmail Drafts for a person to send.")}
        />
      )}
      {invoice && openModal === "chaser" && (
        <DraftChaserModal
          invoiceId={invoice.id}
          onClose={() => setOpenModal(null)}
          onDone={() => actionDone("Chaser drafted and waiting in Gmail Drafts for a person to send.")}
        />
      )}
      {invoice && openModal === "credit-note" && (
        <CreditNoteModal
          invoiceId={invoice.id}
          onClose={() => setOpenModal(null)}
          onDone={() => actionDone("Credit note raised as a draft in Xero.")}
        />
      )}
      {invoice && invoice.customerId && openModal === "statement" && (
        <GenerateStatementModal
          customerId={invoice.customerId}
          customerName={invoice.customerName}
          onClose={() => setOpenModal(null)}
          onDone={() => actionDone("Statement generated and the covering email is waiting in Gmail Drafts for a person to send.")}
        />
      )}
    </div>
  );
}
